import { useNavigate } from "react-router-dom";
import {
  Building2, ChevronRight, Coins, CreditCard, FileText, Landmark,
  Lock, MapPin, MessageSquarePlus, Phone, Plus, RefreshCw, ShieldCheck, Store, User as UserIcon, UserPlus,
} from "lucide-react";
import { clientRoutes } from "@/lib/clientRoutes";
import { uah } from "@/lib/clientFormat";
import { useLegalEntityDetail } from "@/hooks/useClients";
import type { LegalEntityDetail as LE } from "@/api/clients";

/**
 * Этап 4 — Legal-entity (Account) DETAIL PANE. Mounts inside the master-detail
 * «Юрособи» tab (ClientEntitiesTab). Renders the §2.1/§2.2 blocks read-only
 * (1С + BI, source-labelled) and the ★ F-1 fix: live Контакти/Точки slices with
 * «+ Прив'язати контакт» (reuse Этап 2 drawer) / «+ Додати точку 🔒» (gated to
 * Этап 5) + «всі … клієнта» links. Credit-limit/відстрочка are договір-sourced
 * (representative contract) and labelled «за договором», NOT as BI. «Запит на
 * зміну даних» is backend-gated (3B). AR per-LE only — never a holding sum.
 */

// §C — label keyed on the legal-identity axis (NOT partyType, which conflated
// фізособа з ФОП). organization→Юридична особа · fop→ФОП · individual→Фізична особа.
const LEGAL_IDENTITY_LABEL: Record<string, string> = {
  organization: "Юридична особа", fop: "ФОП", individual: "Фізична особа",
  unknown: "Тип не визначено", "": "Тип не визначено",
};
const VAT_LABEL: Record<string, string> = {
  payer: "Платник ПДВ", non_payer: "Не платник ПДВ", unknown: "ПДВ: невідомо",
};
const DQ_LABEL: Record<string, string> = {
  no_legal_id: "Немає ЄДРПОУ/ІПН",
  no_edrpou: "Немає ЄДРПОУ",
  no_ipn: "Немає ІПН",
  no_legal_address: "Немає юр.адреси",
  no_contacts: "Немає контактів",
  overdue_debt: "Прострочений борг",
  type_undefined: "Тип не визначено",
};

function Section({ title, icon, action, children }: {
  title: string; icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2 text-sm font-medium">
        <span className="flex items-center gap-2">{icon}{title}</span>
        {action}
      </div>
      <div className="space-y-2 p-4">{children}</div>
    </div>
  );
}

function KV({ k, v, empty, tag, warn }: { k: string; v?: string | null; empty?: string; tag?: string; warn?: boolean }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {k}{tag && <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">{tag}</span>}
      </span>
      {v ? <span className={"text-right " + (warn ? "font-medium text-warning-foreground" : "text-foreground")}>{v}</span>
        : <span className="text-right text-xs italic text-muted-foreground/50">{empty ?? "—"}</span>}
    </div>
  );
}

function FromSrc({ src }: { src: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium bg-muted text-muted-foreground">
      <Lock className="h-3 w-3" />{src}
    </span>
  );
}

export function LegalEntityDetailPane({
  holdingId,
  accountId,
  onBindContact,
}: {
  holdingId: string;
  accountId: string;
  onBindContact: () => void;
}) {
  const navigate = useNavigate();
  const q = useLegalEntityDetail(accountId);

  if (q.isPending) {
    return <div className="h-72 animate-pulse rounded-xl bg-muted" />;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-xl border border-dashed px-4 py-12 text-center">
        <ShieldCheck className="mx-auto h-7 w-7 text-muted-foreground/50" />
        <h2 className="mt-3 text-base font-semibold">Юрособу не знайдено або немає доступу</h2>
        <p className="mt-1 text-sm text-muted-foreground">Перевірте права доступу.</p>
      </div>
    );
  }

  const le: LE = q.data;
  const id = le.identity;
  const r = le.registry;
  const f = le.finance;
  const aa = le.allowedActions;
  const dq = le.dq ?? [];
  const credit = f.creditLimit !== null
    ? new Intl.NumberFormat("uk-UA").format(f.creditLimit) + " " + (f.currency || "₴")
    : null;
  const biSnap = le.biSnapshotAt ? le.biSnapshotAt.slice(0, 10) : null;

  // §3/§4 LegalContour IA — type-aware contour (Account/1С not changed, only podача).
  const lit = id.legalIdentityType;
  const isIndividual = lit === "individual";
  const isFop = lit === "fop";
  const isOrg = lit === "organization";
  const HeaderIcon = isIndividual ? UserIcon : Building2;
  const contourTitle = isIndividual
    ? "Дані особи"
    : isFop ? "ФОП / Реквізити" : isOrg ? "Юрособа / Організація" : "Юридичні дані";
  const contourIcon = isIndividual
    ? <UserIcon className="h-4 w-4 text-primary" />
    : <Landmark className="h-4 w-4 text-primary" />;
  const locationText = r.legalAddress || r.actualAddress || null;
  const syncLine = r.code1c
    ? `${r.code1c}${r.syncedAt ? ` · ${r.syncedAt.slice(0, 10)}` : ""}`
    : (r.syncedAt ? r.syncedAt.slice(0, 10) : null);

  return (
    <div className="space-y-3">
      {/* header */}
      <div className="rounded-xl border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <HeaderIcon className="h-4 w-4 shrink-0 text-primary" />
              <h2 className="text-lg font-semibold">{id.legalName || id.name}</h2>
              {!id.active && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">неактивна</span>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{LEGAL_IDENTITY_LABEL[id.legalIdentityType] ?? "Тип не визначено"}</span>
              {id.legalForm && <span>· {id.legalForm}</span>}
              {id.tradeName && id.tradeName !== id.name && <span>· ТМ «{id.tradeName}»</span>}
            </div>
          </div>
          {le.sourceSystem && <FromSrc src={`з ${le.sourceSystem} · read-only`} />}
        </div>
        {dq.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {dq.map((flag) => (
              <span key={flag} className="rounded-md bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground">
                ⚠ {DQ_LABEL[flag] ?? flag}
              </span>
            ))}
          </div>
        )}
        {!le.synced && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-warning px-3 py-2 text-xs font-medium text-warning-foreground">
            <RefreshCw className="h-3.5 w-3.5" />Не синхронізовано з 1С — юридичні та фінансові дані можуть бути відсутні.
          </div>
        )}
      </div>

      {/* 1С + BI read-only */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Section title={contourTitle} icon={contourIcon} action={<FromSrc src="🔒 1С" />}>
          {/* §4 — type-aware contour. individual → «Дані особи» (ПІБ · ІПН якщо є ·
              локація · 1С-sync; NO ЄДРПОУ/ПДВ/правова форма/B2B). fop → ІПН (ЄДРПОУ не
              буває); ПДВ ЛИШЕ з 1С (no default «Не платник»). org → повний юр-контур. */}
          <KV k={isIndividual ? "ПІБ" : "Офіційна назва"} v={id.legalName ?? id.name} />

          {isOrg && <KV k="ЄДРПОУ" v={r.edrpou} />}
          {isFop && (
            <>
              <KV k="ІПН" v={r.ipn} />
              <KV k="ЄДРПОУ" v={null} empty="— (ФОП не має)" />
            </>
          )}
          {isIndividual && <KV k="ІПН" v={r.ipn} empty="немає" />}
          {(lit === "unknown" || lit === "") && (
            <>
              <KV k="ЄДРПОУ" v={r.edrpou} />
              <KV k="ІПН" v={r.ipn} />
            </>
          )}

          {/* ПДВ — org always; ФОП лише з 1С (no false default); individual — приховано. */}
          {isOrg && <KV k="ПДВ" v={VAT_LABEL[r.vatStatus] ?? r.vatStatus} />}
          {isFop && (
            r.vatStatus && r.vatStatus !== "unknown"
              ? <KV k="ПДВ" v={VAT_LABEL[r.vatStatus] ?? r.vatStatus} />
              : <KV k="ПДВ" v={null} empty="не вказано (лише з 1С)" />
          )}
          {!isIndividual && r.vatNumber && <KV k="ІПН ПДВ" v={r.vatNumber} />}
          {!isIndividual && <KV k="Система оподаткування" v={r.taxSystem} />}

          {isIndividual ? (
            <>
              <KV k="Локація" v={locationText} empty="не вказано" />
              <KV k="1С-код / sync" v={syncLine} empty="—" />
              <div className="mt-1 rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-[11.5px] text-muted-foreground">
                Фізична особа без правової форми → НЕ показуємо ЄДРПОУ · ПДВ · правову форму ·
                торгові точки/карту як B2B-вимогу. Це не «юрособа». Договори / фінанси доступні, якщо є дані.
              </div>
            </>
          ) : (
            <>
              <KV k="Юридична адреса" v={r.legalAddress} empty="не вказано" />
              <KV k="Фактична адреса" v={r.actualAddress} empty="не вказано" />
            </>
          )}
        </Section>

        <Section
          title={isIndividual ? "Фінанси" : "Фінанси (по юрособі)"}
          icon={<Coins className="h-4 w-4 text-primary" />}
          action={<FromSrc src={biSnap ? `🔒 BI · snapshot ${biSnap}` : "🔒 BI"} />}
        >
          <KV k="Борг" v={f.arBalance !== null ? uah(f.arBalance) : null} empty="без боргу" />
          <KV k="Прострочено" v={f.arOverdue ? uah(f.arOverdue) : null} empty="немає" warn={!!f.arOverdue} />
          <KV k="Днів прострочки" v={f.arOverdueDays ? String(f.arOverdueDays) : null} empty="—" />
          <KV k="Остання оплата" v={f.lastPaymentDate ? f.lastPaymentDate.slice(0, 10) : null} empty="—" />
          <KV k="Останнє замовлення" v={f.lastOrderDate ? f.lastOrderDate.slice(0, 10) : null} empty="—" />
          <div className="my-1 border-t" />
          {/* договір-sourced — labelled «за договором», NOT BI */}
          <div className="flex justify-between gap-3 text-sm">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CreditCard className="h-3.5 w-3.5" />Кредитний ліміт
              <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">за договором</span>
            </span>
            {credit ? <span className="text-right">{credit}</span> : <span className="text-right text-xs italic text-muted-foreground/50">—</span>}
          </div>
          <KV k="Відстрочка" tag="за договором" v={f.deferralDays !== null ? `${f.deferralDays} дн.` : null} empty="—" />
        </Section>
      </div>

      {/* Договори (link · 3.0C) */}
      <Section title={`Договори (${le.contracts.length})`} icon={<FileText className="h-4 w-4 text-primary" />} action={<FromSrc src="link · 3.0C" />}>
        {le.contracts.length === 0 ? (
          <div className="text-xs italic text-muted-foreground">Договорів ще немає</div>
        ) : (
          <div className="space-y-1.5">
            {le.contracts.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(clientRoutes.contract(holdingId, c.id))}
                className="flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2 text-left hover:border-primary/40"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{c.num || "Договір"}</span>
                    {c.source === "sync_1c" && (
                      <span title="Дзеркало з 1С" className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">з 1С</span>
                    )}
                    {c.type && <span className="text-xs text-muted-foreground">{c.type}</span>}
                  </div>
                  {c.validUntil && <span className="text-xs text-muted-foreground">до {c.validUntil}</span>}
                </div>
                {c.status && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{c.status}</span>}
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
              </button>
            ))}
          </div>
        )}
      </Section>

      {/* ★ F-1 — Контакти + Точки live slices. BUG3: для individual без «юрособа»-
          термінології і БЕЗ блоку «Точки» (B2B-вимога не застосовна). */}
      <div className={`grid grid-cols-1 gap-3 ${isIndividual ? "" : "md:grid-cols-2"}`}>
        <Section
          title={isIndividual ? `Контакти (${le.contacts.length})` : `Контакти цієї юрособи (${le.contacts.length})`}
          icon={<UserIcon className="h-4 w-4 text-primary" />}
          action={aa.canBindContact ? (
            <button onClick={onBindContact} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              <UserPlus className="h-3.5 w-3.5" />Прив'язати контакт
            </button>
          ) : undefined}
        >
          {le.contacts.length === 0 ? (
            <div className="text-xs italic text-muted-foreground">{isIndividual ? "Контактів немає." : "Контактів цієї юрособи немає."} {aa.canBindContact && "Натисніть «Прив'язати контакт»."}</div>
          ) : (
            <div className="space-y-1.5">
              {le.contacts.map((c, i) => (
                <div key={c.id ?? i} className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                    {(c.name || "?").slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm">{c.name}</span>
                      {c.primary && <span className="rounded bg-primary/10 px-1 py-0.5 text-[10px] font-medium text-primary">основний</span>}
                    </div>
                    {c.role && <span className="text-xs text-muted-foreground">{c.role}</span>}
                  </div>
                  {c.phone && <a href={`tel:${c.phone}`} className="text-muted-foreground hover:text-primary"><Phone className="h-3.5 w-3.5" /></a>}
                </div>
              ))}
            </div>
          )}
          <button onClick={() => navigate(clientRoutes.contacts(holdingId))} className="mt-1 text-xs font-semibold text-primary hover:underline">
            Всі контакти клієнта →
          </button>
        </Section>

        {!isIndividual && <Section
          title="Точки цієї юрособи"
          icon={<Store className="h-4 w-4 text-primary" />}
          action={
            <button
              type="button"
              disabled
              title={aa.outletDisabledReason ?? "Доступно після Этапу 5"}
              className="inline-flex cursor-not-allowed items-center gap-1 text-xs font-medium text-muted-foreground/50"
            >
              <Plus className="h-3 w-3" />Додати точку <Lock className="h-3 w-3" />
            </button>
          }
        >
          {le.outlets.length === 0 ? (
            <div className="text-xs italic text-muted-foreground">Торгових точок цієї юрособи немає.</div>
          ) : (
            <div className="space-y-1.5">
              {le.outlets.map((o) => (
                <div key={o.id} className="flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-info text-info-foreground">
                    <Store className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm">{o.name || "Точка"}</span>
                      {!o.active && <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">неактивна</span>}
                    </div>
                    {o.location && <span className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{o.location}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => navigate(`/clients/${holdingId}/outlets`)} className="mt-1 text-xs font-semibold text-primary hover:underline">
            Всі точки клієнта →
          </button>
        </Section>}
      </div>

      {/* 1С/BI source + change-request (gated, backend-owned reason) */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          {le.synced
            ? `${isIndividual ? "Дані особи та фінанси" : "Юридичні та фінансові дані"} — з 1С/BI${r.syncedAt ? ` · синхронізовано ${r.syncedAt.slice(0, 10)}` : ""}${r.code1c ? ` · код 1С ${r.code1c}` : ""}`
            : "Запис не синхронізовано з 1С"}
        </span>
        <button
          type="button"
          disabled={!aa.canRequestChange}
          title={aa.canRequestChange ? undefined : (aa.changeDisabledReason ?? "Недоступно")}
          className="inline-flex cursor-not-allowed items-center gap-1 text-[11px] text-muted-foreground/50"
        >
          <MessageSquarePlus className="h-3 w-3" />Запит на зміну даних {!aa.canRequestChange && <Lock className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
